/**
 * Campaign Views - View Creation Utilities
 * 
 * Functions for creating and duplicating custom views.
 * 
 * UNIFIED VIEW MODEL:
 * All views are equal - there's no distinction between "default" and "custom".
 * When a campaign is created, it gets 6 initial views (Flow, Kanban, Table,
 * Gantt, Todo, Astra). Multi-views get just 1 view (the type chosen at creation).
 * 
 * Any view can be deleted as long as at least one view remains.
 */

import { getDefaultCardDisplaySettings } from '../../config/cardDisplayConfig';
import { getDefaultVisibilityForView } from '../../config/fieldVisibility';
import { VIEW_TYPE_LABELS, DEFAULT_VIEW_SETTINGS, VIEW_TYPES } from './constants';

const CARD_DISPLAY_VIEWS = new Set([VIEW_TYPES.FLOW, VIEW_TYPES.KANBAN, VIEW_TYPES.NEURAL]);

function buildDefaultSettings(type) {
  const settings = JSON.parse(JSON.stringify(DEFAULT_VIEW_SETTINGS[type] || {}));
  if (CARD_DISPLAY_VIEWS.has(type)) {
    settings.cardDisplaySettings = getDefaultCardDisplaySettings();
  }
  if (type === VIEW_TYPES.TABLE) {
    settings.columnVisibility = getDefaultVisibilityForView('table');
  }
  if (type === VIEW_TYPES.TODOLIST) {
    settings.elementVisibility = getDefaultVisibilityForView('todolist');
  }
  return settings;
}

/**
 * Generate a unique view ID
 * @returns {string}
 */
export function generateViewId() {
  return `view-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new view with default settings
 * @param {string} type - View type (flow, kanban, table, gantt, todolist)
 * @param {string} [name] - Optional custom name
 * @returns {Object} New view object
 */
export function createCustomView(type, name) {
  const displayName = name || VIEW_TYPE_LABELS[type];

  return {
    id: generateViewId(),
    type,
    name: displayName,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    settings: buildDefaultSettings(type),
  };
}

/**
 * Create the 6 initial views for a new campaign
 * These are the standard Flow, Kanban, Table, Gantt, Todo, Astra views
 * @returns {Object[]} Array of 6 view objects
 */
export function createDefaultCampaignViews() {
  const viewTypes = [
    VIEW_TYPES.FLOW,
    VIEW_TYPES.KANBAN,
    VIEW_TYPES.TABLE,
    VIEW_TYPES.GANTT,
    VIEW_TYPES.TODOLIST,
    VIEW_TYPES.NEURAL,
  ];
  
  return viewTypes.map(type => createCustomView(type, VIEW_TYPE_LABELS[type]));
}

/**
 * Duplicate an existing view
 * @param {Object} view - View to duplicate
 * @returns {Object} New view object with copied settings
 */
export function duplicateView(view) {
  const { id, createdAt, updatedAt, ...rest } = view;
  return {
    ...rest,
    id: generateViewId(),
    name: `${view.name} (Copy)`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}
